import { useEffect, useState } from "react";
import Button from "../components/button/Button.tsx";
import SingleUser from "../components/settings/single-user.tsx";
import axiosInstance from "../utils/axios-instance.ts";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/parse-errors.ts";
import UseAuth from "../utils/use-auth.ts";
import { settingsNavData } from "../components/settings/settings-data.ts";
import clsx from "clsx";
import UserSettingsTab from "../components/settings/user-settings.tsx";
import PaymentSettings from "../components/settings/payment-settings.tsx";
import AgencySettings from "../components/settings/agency-settings.tsx";
import { UserModelType } from "../types/user-types.ts";
import PaginationMenu from "../components/pagination/paginationMenu.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import GptSettings from "../components/settings/gpt-settings.tsx";
import Header from "../layout/Header.tsx";
import StatsSetting from "../components/settings/stats-settings.tsx";


interface IUsers {
  totalCount: number;
  data: UserModelType[]
}

const UserSettings = () => {
  const { user } = UseAuth()
  const [active, setActive] = useState<"user-settings" | "agency-users" | "agency-settings" | "gpt-settings" | "stats-settings" | "payment-settings">("user-settings")
  const [showUserForm, setShowUserForm] = useState<boolean>(false);
  const [users, setUsers] = useState<IUsers>({ totalCount: 0, data: [] })
  const [limit,] = useState(10)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [newUserValues, setNewUserValues] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState({
    isUpdatingUser: false,
    isCreatingUser: false,
    isFetchingUser: false
  });


  const HandleCreateUser = async () => {
    if (validate()) {
      setLoading((prev) => ({ ...prev, isCreatingUser: true }))
      try {
        const res = await axiosInstance.post('/auth/create', { ...newUserValues })
        toast.success("User Added Successfully")
        setUsers((prev) => ({ totalCount: prev.totalCount + 1, data: [...prev.data, res.data.data] }))
        setShowUserForm(false)
        setLoading((prev) => ({ ...prev, isCreatingUser: false }))
      } catch (error) {
        console.log(error)
        toast.error(ParseErrors(error))
        setLoading((prev) => ({ ...prev, isCreatingUser: false }))
      }
    }
  }


  useEffect(() => {
    const getUsers = async () => {
      setLoading((prev) => ({ ...prev, isFetchingUser: true }))
      try {
        const res = await axiosInstance.get(`/user/my/agency?page=${page}&limit=${limit}${search ? `&name=${search}` : ''}`)
        setUsers(res.data)
        setLoading((prev) => ({ ...prev, isFetchingUser: false }))
      } catch (error) {
        console.log(error)
        setLoading((prev) => ({ ...prev, isFetchingUser: false }))
      }
    }

    user?.type === "agency" && getUsers()
  }, [user?.type, page, limit, search])


  const HandleNewUserValues = (e) => {
    setNewUserValues((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }


  const validate = () => {
    if (!newUserValues.name) {
      toast.error("Please enter name")
      return false
    } else if (!newUserValues.email) {
      toast.error("Please enter email address")
      return false
    } else if (!newUserValues.email?.includes('@')) {
      toast.error("Please enter a valid email address")
      return false
    } else if (!newUserValues.password) {
      toast.error("Please enter password")
      return false
    } else if (newUserValues?.password !== newUserValues?.cpassword) {
      toast.error("Please enter a valid password")
      return false
    } else {
      return true
    }
  }

  const HandleChangePage = (page: number) => {
    setPage(page);
  }

  const inputClass = "h-[44px] w-full border border-gray-200 rounded-xl outline-none px-4 text-[14.5px] font-normal placeholder-gray-400 bg-white";

  return (
    <div className="max-w-[1440px] mx-auto bg-white min-h-screen">
      <Header className={"bg-white"} />

      {/* Tabs */}
      <div className="py-8 bg-surface border-b border-gray-100">
        <div className="mx-auto w-[95%] max-w-5xl flex items-center justify-center gap-2 flex-wrap">
          {
            settingsNavData.map((item) => {
              return item.permissions?.includes(user?.type)
                ? (
                  <button
                    key={item.id}
                    onClick={() => setActive(item.id as any)}
                    className={clsx(
                      "px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-200",
                      item.id === active
                        ? "bg-blue text-white shadow-soft"
                        : "bg-white text-lightBlack border border-gray-100 hover:border-blue/40 hover:text-blue"
                    )}
                  >
                    {item.label}
                  </button>
                )
                : null
            })
          }
        </div>
      </div>

      <div className="min-h-screen bg-white">

        {/* --------------------- USER SETTINGS ----------------------- */}
        {active === "user-settings" ? <UserSettingsTab /> : null}
        {/* --------------------- USER SETTINGS ----------------------- */}

        {/* --------------------- Payment SETTINGS ----------------------- */}
        {active === "payment-settings" && user.type === "agency" ? <PaymentSettings /> : null}
        {/* --------------------- Payment SETTINGS ----------------------- */}

        {/* --------------------- AGENCY SETTINGS ----------------------- */}
        {active === "agency-settings" && user.type === "super-admin" ? <AgencySettings /> : null}
        {/* --------------------- AGENCY SETTINGS ----------------------- */}

        {/* --------------------- AGENCY SETTINGS ----------------------- */}
        {active === "gpt-settings" && user.type === "super-admin" ? <GptSettings /> : null}
        {/* --------------------- AGENCY SETTINGS ----------------------- */}

        {/* --------------------- AGENCY SETTINGS ----------------------- */}
        {active === "stats-settings" && user.type === "super-admin" ? <StatsSetting /> : null}
        {/* --------------------- AGENCY SETTINGS ----------------------- */}



        {/* --------------------- AGENCY USERS DATA ----------------------- */}
        {user?.type === "agency" && active === "agency-users" && (
          <div className="w-full lg:w-[60%] mx-auto py-12">
            <div className="w-[92%] mx-auto">
              <div className="flex flex-col items-start gap-5">
                <div className="w-full flex items-center justify-between gap-4">
                  <h2 className="font-display text-xl text-lightBlack font-semibold tracking-tight">
                    Agency Users <span className="text-gray-400 font-normal">({users.totalCount})</span>
                  </h2>
                  <input
                    placeholder="Search users…"
                    onChange={(e) => setSearch(e.target.value.toLocaleLowerCase())}
                    className={`${inputClass} max-w-xs`}
                  />
                </div>

                {showUserForm && (
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                    <input placeholder="User Name" name="name" onChange={HandleNewUserValues} className={inputClass} />
                    <input placeholder="Email" name="email" onChange={HandleNewUserValues} className={inputClass} />
                    <input placeholder="Password" name="password" onChange={HandleNewUserValues} className={inputClass} />
                    <input placeholder="Confirm Password" name="cpassword" onChange={HandleNewUserValues} className={inputClass} />
                  </div>
                )}

                <div className="w-full md:w-auto">
                  <Button
                    isLoading={loading.isCreatingUser}
                    disabled={loading.isCreatingUser}
                    className="!w-full md:!w-auto !px-8"
                    onClick={() => {
                      if (!showUserForm) {
                        setShowUserForm(true)
                      } else {
                        HandleCreateUser()
                      }
                    }}
                  >
                    {showUserForm ? "Submit" : "+ Add User"}
                  </Button>
                </div>
              </div>

              {/* ------------------------ USERS LIST --------------------- */}
              <div className="w-full mt-10">
                {loading.isFetchingUser ? (
                  <div className="w-full h-[300px] flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-5xl text-blue" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {users.data?.map((item) => (
                      <SingleUser key={item?.id} item={item} />
                    ))}
                  </div>
                )}

                <div className="mt-8">
                  <PaginationMenu totalCount={users.totalCount} pageSize={limit} currentPage={page} onPageChange={HandleChangePage} />
                </div>
              </div>
            </div>
          </div>
        )}
        {/* --------------------- AGENCY USERS DATA ----------------------- */}
      </div>

    </div>
  );
};

export default UserSettings;
